import React, { useState } from 'react';
import { Button, ProgressBar, Alert, Modal, Form } from 'react-bootstrap';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/config';

// ── useUploadFiles now lives in useLeads.js, no more Redux ───────────────────
import { useUploadFiles } from '../hooks/useLeads';

const FileUploader = ({ clientId, folder, availableSubFolders = [] }) => {
  const [selectedFiles,    setSelectedFiles]    = useState([]);
  const [selectedSubFolder, setSelectedSubFolder] = useState(availableSubFolders[0] || '');
  const [showModal,        setShowModal]        = useState(false);
  const [uploadProgress,   setUploadProgress]   = useState({});
  const [uploadError,      setUploadError]      = useState(null);

  // isPending replaces the old isLoading (TanStack Query v5)
  const { mutateAsync: updateHistory, isPending } = useUploadFiles(clientId);

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
    setUploadError(null);
  };

  const uploadToFirebase = (file) =>
    new Promise((resolve, reject) => {
      const path        = `${clientId}/${folder}/${selectedSubFolder}/${Date.now()}_${file.name}`;
      const storageRef  = ref(storage, path);
      const uploadTask  = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snap) => {
          const pct = (snap.bytesTransferred / snap.totalBytes) * 100;
          setUploadProgress(prev => ({ ...prev, [file.name]: pct }));
        },
        reject,
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve({ name: file.name, size: file.size, type: file.type, path, downloadURL });
        }
      );
    });

  const handleUpload = async () => {
    if (!selectedFiles.length || !selectedSubFolder) return;
    setUploadError(null);

    try {
      const uploaded = await Promise.all(selectedFiles.map(uploadToFirebase));

      // POST to /api/update-history → server writes Firestore record
      // AND emits  files:updated  via socket → TabContent auto-refetches
      await updateHistory({
        clientId,
        folder,
        subFolder: selectedSubFolder,
        username:  localStorage.getItem('projectUserDisplayName'),
        attachments: uploaded.map(f => ({
          fileName:    f.name,
          fileSize:    f.size,
          fileType:    f.type,
          filePath:    f.path,
          downloadURL: f.downloadURL,
        })),
      });

      // Reset state after successful upload
      setSelectedFiles([]);
      setUploadProgress({});
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadError(err.message);
    }
  };

  const handleUploadClick = () => {
    if (!selectedFiles.length) return;
    // Show subfolder picker only when there are multiple choices
    availableSubFolders.length > 1 ? setShowModal(true) : handleUpload();
  };

  const totalProgress = Object.values(uploadProgress).length
    ? Object.values(uploadProgress).reduce((s, v) => s + v, 0) / Object.values(uploadProgress).length
    : 0;

  const fmt = (b) =>
    b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`;

  return (
    <div className="file-uploader-container">
      <input
        type="file"
        multiple
        id={`fileUpload-${folder}`}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <div className="d-flex gap-2 align-items-center">
        <Button
          variant="outline-primary"
          disabled={isPending}
          onClick={() => document.getElementById(`fileUpload-${folder}`).click()}
        >
          Select Files
        </Button>
        <Button
          variant="primary"
          disabled={isPending || !selectedFiles.length}
          onClick={handleUploadClick}
        >
          {isPending ? 'Uploading…' : `Upload ${selectedFiles.length} file${selectedFiles.length !== 1 ? 's' : ''}`}
        </Button>
      </div>

      {selectedFiles.length > 0 && (
        <div className="mt-3">
          <strong>Selected files:</strong>
          <ul className="list-unstyled">
            {selectedFiles.map((f, i) => (
              <li key={i} className="mt-1">
                {f.name} ({fmt(f.size)})
                {uploadProgress[f.name] != null && (
                  <ProgressBar
                    now={uploadProgress[f.name]}
                    label={`${Math.round(uploadProgress[f.name])}%`}
                    className="mt-1"
                    style={{ height: '6px' }}
                  />
                )}
              </li>
            ))}
          </ul>
          {Object.keys(uploadProgress).length > 0 && (
            <>
              <strong>Total:</strong>
              <ProgressBar now={totalProgress} label={`${Math.round(totalProgress)}%`} className="mt-1" />
            </>
          )}
        </div>
      )}

      {uploadError && (
        <Alert variant="danger" className="mt-3">Upload failed: {uploadError}</Alert>
      )}

      {/* Subfolder picker modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Select Destination Subfolder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label><strong>Choose subfolder:</strong></Form.Label>
            <Form.Select
              value={selectedSubFolder}
              onChange={(e) => setSelectedSubFolder(e.target.value)}
              className="mb-3"
            >
              {availableSubFolders.map(sf => (
                <option key={sf} value={sf}>{sf}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <strong>Files ({selectedFiles.length}):</strong>
          <ul className="mt-2">
            {selectedFiles.map((f, i) => <li key={i}>{f.name} ({fmt(f.size)})</li>)}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!selectedSubFolder}
            onClick={() => { setShowModal(false); handleUpload(); }}
          >
            Upload to {selectedSubFolder}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FileUploader;